/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import Modal from '~/components/Modal'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../../root'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import TaskManageDocsTable from './TaskManageDocsTable'
import { Task } from '../../../../services/api/types'

interface TaskManageDocsModalProps {
  task: any
  onClose?: () => void
}

/**
 * Task Manage Documents Modal
 * Shows all files in a multi-file workflow with actions for each
 * Migrated from: lego/page/tasks/manage-docs/Modal.jsx
 */
export default function TaskManageDocsModal({ task, onClose }: TaskManageDocsModalProps) {
  const { t } = useTranslation()
  const { api } = useServiceContext()
  const [loading, setLoading] = useState(true)
  const [taskDetails, setTaskDetails] = useState<Task | null>(null)
  const [workflowDetails, setWorkflowDetails] = useState<any>(null)

  useEffect(() => {
    const loadTaskAndWorkflow = async (taskData: Task) => {
      setTaskDetails(taskData)

      if (taskData.workflowId) {
        try {
          const wfData = await api.workflows.getWorkflow(taskData.workflowId)
          setWorkflowDetails(wfData)
          setLoading(false)
        } catch (error) {
          console.error('Failed to load workflow details:', error)
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    }

    // When there are no flags or workflowStepType in the task, download full task detail
    if (!task.flags || !task.workflowStepType) {
      api.tasks
        .getTask(String(task.id))
        .then((data) => {
          loadTaskAndWorkflow(data)
        })
        .catch((error) => {
          console.error('Failed to load task details:', error)
          setLoading(false)
        })
    } else {
      loadTaskAndWorkflow(task)
    }
  }, [task, api])

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <Modal
      className="theme-sandman"
      show={true}
      handleClose={handleClose}
      title={t('TASK_FILES_COMPARE_H_TITLE')}
      size="md"
    >
      <Modal.Body>
        {loading ? (
          <LoadingSpinner centered size={40} />
        ) : (
          <TaskManageDocsTable workflow={workflowDetails} task={taskDetails} doClose={handleClose} />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleClose}
          id="bf92e0d6-a49f-468e-8de8-cb40c57ac25e"
          className="text-capitalize"
        >
          {t('OK')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
